import { DEEP_RESEARCH_LEAD_CREDITS } from "@signalist/shared-types";

import { FunctionComponent, useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";

import { Link } from "react-router-dom";

import { Modal } from "@/components";
import { useEmbeddedCreditCost } from "@/components/CreditConfirmModal";
import { AiApplyType, PriorityFilterStatus, SelectType } from "@/enum/list.enum";
import { Dropdown, AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { listSelector } from "@/pages/Private/redux/list/list.slice";
import { useAppSelector } from "@/redux/hooks";
import { ERROR_TYPE, renderErrorMessages } from "@/utils";
import {
	useGetDeepResearchPromptsQuery,
	useExecuteBulkDeepResearchListMutation,
} from "@/pages/Private/redux/deepResearch/deepResearch.api";

import { useDeepResearchSocketContext } from "@/pages/Private/pages/ProfileTracker/providers/DeepResearchSocketContext";

import { List } from "../../../schema/list";
import {
	buildDeepResearchBulkListPayload,
	DeepResearchBulkListPayloadFilters,
} from "./deepResearchBulkListPayload";
import { deepResearchRunScope, runScopeCount } from "./customColumnRunScope";

export interface DeepResearchModalProps {
	setShowConfirm?: (value: boolean) => void;
	open: boolean;
	listId: number;
	setOpen: (value: boolean) => void;
	selectedProspectIds?: number[];
	/** Not read for the cost: the run scope counts what the run covers (PREC-4263 review S1). */
	list?: List;
	filters?: DeepResearchBulkListPayloadFilters;
	priorityFilterStatus?: PriorityFilterStatus[];
	isOnlySelected?: boolean;
	isAllSelected?: boolean;
	onSuccess?: (result: { queued: number; skipped: number; total: number }) => void;
	onError?: (error: { status?: number; data?: { message?: string } }) => void;
}

export const DeepResearchModal: FunctionComponent<DeepResearchModalProps> = ({
	setShowConfirm,
	open,
	listId,
	setOpen,
	selectedProspectIds,
	filters,
	priorityFilterStatus,
	isOnlySelected = false,
	isAllSelected = false,
	onSuccess,
	onError,
}) => {
	const { t } = useTranslation();
	const ts = useCallback(
		(key: string, options?: Record<string, unknown>) => t(`aiColumn.${key}`, options),
		[t]
	);
	const { startPending } = useDeepResearchSocketContext();

	const [applyTo, setApplyTo] = useState<AiApplyType>(
		isOnlySelected ? AiApplyType.CURRENTLY_SELECTED : AiApplyType.NONE
	);

	const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);

	const { inbox, meta } = useAppSelector(listSelector);

	const { data: promptsData } = useGetDeepResearchPromptsQuery({ page: 1, limit: 100 });
	const [executeBulk, { isLoading, error, reset }] = useExecuteBulkDeepResearchListMutation({
		fixedCacheKey: "execute-bulk-deep-research-list",
	});
	const prompts = promptsData?.data || [];
	const activePromptId = selectedPromptId ?? prompts[0]?.id ?? null;

	const listPersonIds = useMemo(() => {
		if (isOnlySelected) {
			return isAllSelected ? [] : (selectedProspectIds ?? []);
		}

		if (applyTo === AiApplyType.FIRST_10) {
			return inbox.slice(0, 10).map((p) => p.id);
		}

		if (applyTo === AiApplyType.CURRENTLY_SELECTED) {
			return selectedProspectIds ?? [];
		}

		return [];
	}, [applyTo, inbox, selectedProspectIds, isOnlySelected, isAllSelected]);

	const selectType = useMemo(() => {
		if (isOnlySelected) {
			return isAllSelected ? SelectType.ALL : SelectType.CURRENT_PAGE;
		}

		if (applyTo === AiApplyType.FIRST_10 || applyTo === AiApplyType.CURRENTLY_SELECTED) {
			return SelectType.CURRENT_PAGE;
		}

		return SelectType.ALL;
	}, [applyTo, isOnlySelected, isAllSelected]);

	// The leads the run covers, as the server resolves the payload below (PREC-4263 review S1).
	const scope = deepResearchRunScope({
		applyTo,
		isOnlySelected,
		isAllSelected,
		loadedRowCount: inbox.length,
		selectedCount: listPersonIds.length,
		viewCount: meta?.itemCount ?? 0,
	});

	const cost = useEmbeddedCreditCost({
		active: open,
		scope,
		unitCredits: DEEP_RESEARCH_LEAD_CREDITS,
		copy: "deepResearch",
	});

	const applyToButtonText =
		applyTo === AiApplyType.NONE && !isOnlySelected
			? ts(`button.${AiApplyType.NONE}`)
			: scope.kind === "unknown"
				? t("creditConfirm.deepResearch.confirmUnknown")
				: t("creditConfirm.deepResearch.confirm", { count: runScopeCount(scope) });
	const onSubmit = async () => {
		if (!activePromptId || applyTo === AiApplyType.NONE) {
			return;
		}

		try {
			const result = await executeBulk(
				buildDeepResearchBulkListPayload({
					listId,
					listPersonIds,
					selectType,
					deepResearchPromptId: activePromptId,
					filters,
					priorityFilterStatus,
				})
			).unwrap();

			// Mark prospects as pending for FIRST_10 / CURRENTLY_SELECTED
			if (applyTo === AiApplyType.FIRST_10 || applyTo === AiApplyType.CURRENTLY_SELECTED) {
				const matchedPersons = inbox.filter((p) => listPersonIds.includes(p.id));

				for (const lp of matchedPersons) {
					if (lp.prospect?.id) {
						startPending(lp.prospect.id);
					}
				}
			}

			onSuccess?.(result);

			setOpen(false);
		} catch (err) {
			onError?.(err as { status?: number; data?: { message?: string } });
		}
	};

	const apiErrors = error
		? ([
				"message" in error ? (error as { message: string }).message : "An error occurred",
			] as ERROR_TYPE[])
		: [];

	const promptDropdownData = useMemo(
		() =>
			prompts.map((p) => ({
				title: p.name,
				id: p.id.toString(),
			})),
		[prompts]
	);

	const hasPrompts = prompts.length > 0;

	return (
		<Modal
			handleClose={() => {
				reset();
				if (setShowConfirm) {
					setShowConfirm(true);
				} else if (!setShowConfirm && setOpen) {
					setOpen(false);
				}
			}}
			handleSave={onSubmit}
			isLoading={isLoading}
			isOpened={open}
			size="xs"
			submitButtonText={applyToButtonText}
			title={t("creditConfirm.deepResearch.title")}
			disableSave={
				!hasPrompts || !activePromptId || applyTo === AiApplyType.NONE || !cost.canConfirm
			}
		>
			<div className="flex flex-col space-y-4">
				{!hasPrompts && (
					<div className="flex flex-col gap-2 p-3 rounded-lg bg-brand-white text-sm text-text-meta">
						<span>{t("aiColumn.noPromptsConfigured")}</span>
						<Link className="text-blue-action underline" to="/templates/deep-research-prompts">
							{t("aiColumn.createFirstPrompt")}
						</Link>
					</div>
				)}
				<Dropdown
					data={promptDropdownData}
					floating={false}
					handleSelect={(value?: AutoCompleteItem) => {
						if (value?.id) {
							setSelectedPromptId(Number(value.id));
						}
					}}
					label="Deep Research Prompt"
					value={
						activePromptId
							? promptDropdownData.find((p) => p.id === activePromptId.toString())
							: undefined
					}
				/>

				{apiErrors.length ? renderErrorMessages(apiErrors) : null}
			</div>
			{!isOnlySelected && (
				<div className="flex flex-row mt-5 border-t pt-5 border-gray-200">
					<Dropdown
						data={[
							{
								title: ts(AiApplyType.NONE),
								id: AiApplyType.NONE,
							},
							{
								title: ts(AiApplyType.FIRST_10),
								id: AiApplyType.FIRST_10,
							},
							{
								title: ts(AiApplyType.CURRENTLY_FILTERED),
								id: AiApplyType.CURRENTLY_FILTERED,
							},
							{
								title: ts(AiApplyType.CURRENTLY_SELECTED),
								id: AiApplyType.CURRENTLY_SELECTED,
							},
							{
								title: ts(AiApplyType.ALL),
								id: AiApplyType.ALL,
							},
							{
								title: ts(AiApplyType.ALL_INCLUDING_ASSIGNED),
								id: AiApplyType.ALL_INCLUDING_ASSIGNED,
							},
						]}
						floating={false}
						handleSelect={function (value?: AutoCompleteItem) {
							if (value?.id) {
								setApplyTo(value.id as AiApplyType);
							}
						}}
						label="Apply to ..."
						value={applyTo ? { title: ts(applyTo), id: applyTo } : undefined}
					/>
				</div>
			)}

			{cost.block}
		</Modal>
	);
};
